/**
 * Structured error logging for the app's error surfaces (ErrorBoundary,
 * route-level boundary, and the global window handlers).
 *
 * There is intentionally NO monitoring SDK wired in here. This logs to the
 * console in a single, structured shape so that when we do adopt a monitoring
 * service (Sentry, Datadog, etc.) the only change is the body of `logError` —
 * every call site already passes a normalized payload, so nothing else moves.
 */

/** Where the error originated, so a downstream logger can route/tag it. */
export type ErrorSource =
  | 'react-error-boundary'
  | 'route-error-boundary'
  | 'window.onerror'
  | 'window.unhandledrejection';

/** Normalized, serializable shape every error surface produces. */
export type NormalizedError = {
  source: ErrorSource;
  name: string;
  message: string;
  stack?: string;
  /** React component stack, when the source is an error boundary. */
  componentStack?: string;
  timestamp: string;
};

/**
 * Coerce anything thrown/rejected (Error, string, DOMException, arbitrary
 * object, undefined) into a stable, serializable shape. Never throws.
 */
export function normalizeError(
  error: unknown,
  source: ErrorSource,
  componentStack?: string,
): NormalizedError {
  const base = {
    source,
    componentStack: componentStack || undefined,
    timestamp: new Date().toISOString(),
  };

  if (error instanceof Error) {
    return {
      ...base,
      name: error.name || 'Error',
      message: error.message || String(error),
      stack: error.stack,
    };
  }

  if (typeof error === 'string') {
    return { ...base, name: 'Error', message: error };
  }

  // Objects/undefined/etc. — stringify defensively so we never lose the value.
  let message: string;
  try {
    message = error === undefined ? 'undefined' : JSON.stringify(error);
  } catch {
    message = String(error);
  }
  return { ...base, name: 'NonError', message };
}

/**
 * The single logging seam. Swap the console call for a real logger/monitoring
 * transport later without touching any call site.
 */
export function logError(normalized: NormalizedError): void {
  // eslint-disable-next-line no-console
  console.error('[app-error]', normalized);
}

/** Convenience: normalize then log in one call. */
export function reportError(
  error: unknown,
  source: ErrorSource,
  componentStack?: string,
): NormalizedError {
  const normalized = normalizeError(error, source, componentStack);
  logError(normalized);
  return normalized;
}
